type ParameterInput = {
  id?: string;
  key: string;
  settings: string;
  inputs: Record<string, any>[];
  enabled?: boolean;
};

type ParameterDefinitionField = {
  id: string;
  key: string;
};

type ParameterDefinition = {
  id: string;
  key: string;
  fields: ParameterDefinitionField[];
};

type AdInput = {
  id?: string;
  name: string;
  mediaType: string;
};

type AdType = {
  id?: string;
  publisher: string | null;
  publisherId?: string;
  adInputs: AdInput[];
};

type CustomParameter = {
  id?: string;
  key: string;
  name: string;
  type: string;
  dropdownOptions?: string[];
  setting: string;
  values: any[];
  enabled?: boolean;
};

type HelperInput = {
  adCategory: string;
  publisherType: string;
  publishers: Array<{ id?: string; name: string }>;
  adTypes: AdType[];
  parametersInput: ParameterInput[];
  parametersData: any;
  customParameters: any;
};

type CampaignOutput = {
  name: string;
  publisher_type: string;
  publishers: string[];
  ad_types: any[];
};

type UpdateCampaignOutput = {
  name: string;
  publisher_type: string;
  publishers: Array<{ id?: string; name: string }>;
  ad_types: any[];
};

function convertToAPIFormat(helperInput: HelperInput): CampaignOutput {
  const {
    adCategory,
    publisherType,
    publishers,
    adTypes,
    parametersInput,
    parametersData,
    customParameters,
  } = helperInput;

  function findParamDef(
    mediaType: string,
    groupKey: string
  ): ParameterDefinition | undefined {
    const defs = parametersData[mediaType.toLowerCase()] || [];
    return defs.find((def) => def.key === groupKey);
  }

  function buildParametersForInput(
    publisher: string | null,
    inputName: string,
    mediaType: string
  ) {
    const prefix = publisher
      ? `${publisher}_${inputName}`
      : `null_${inputName}`;
    const matchedParams = parametersInput.filter((param) =>
      param.key.startsWith(prefix)
    );

    return matchedParams
      .map((param) => {
        const groupKey = param.key.replace(`${prefix}_`, "");
        const paramDef = findParamDef(mediaType, groupKey);
        if (!paramDef) return null;

        // Skip params with no setting selected — backend requires value_type
        if (!param.settings || param.settings.trim() === "") return null;

        const inputs = param.inputs || [];
        const isUserDefined = param.settings === "user_defined";
        const isMultiPublisher = param.settings === "multi_publisher";
        const isSinglePublisher = param.settings === "single_publisher";

        if (isMultiPublisher) {
          const groupedInputs: Record<string, any[]> = {};

          inputs.forEach((inputObj) => {
            for (const key in inputObj) {
              if (!groupedInputs[key]) groupedInputs[key] = [];
              groupedInputs[key].push(inputObj[key]);
            }
          });

          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: groupedInputs[field.key] || [],
          }));

          return {
            parameter_definition_id: paramDef.id,
            group_key: groupKey,
            value_type: param.settings,
            fields,
            inputs: groupedInputs,
            is_enabled: param.enabled !== false,
          };
        }

        if (isUserDefined) {
          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
          }));

          return {
            parameter_definition_id: paramDef.id,
            group_key: groupKey,
            value_type: param.settings,
            fields,
            inputs: {},
            is_enabled: param.enabled !== false,
          };
        }

        if (isSinglePublisher) {
          const flatInput = inputs[0] || {};
          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: flatInput[field.key] ?? null,
          }));

          return {
            parameter_definition_id: paramDef.id,
            group_key: groupKey,
            value_type: param.settings,
            fields,
            inputs: flatInput,
            is_enabled: param.enabled !== false,
          };
        }

        const fieldsArr = inputs.map((inputObj) =>
          paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: inputObj[field.key] ?? null,
          }))
        );

        return {
          parameter_definition_id: paramDef.id,
          group_key: groupKey,
          value_type: param.settings,
          fields: fieldsArr[0] || [],
          inputs,
          is_enabled: param.enabled !== false,
        };
      })
      .filter(Boolean);
  }

  function buildCustomParameters(
    customParams: CustomParameter[],
    publisher: string | null,
    name: string
  ) {
    const prefix = publisher ? `${publisher}_${name}_` : `null_${name}_`;

    return customParams
      .filter((cp) => cp.key.startsWith(prefix))
      .map((cp) => ({
        key: cp.key,
        name: cp.name,
        type: cp.type,
        dropdown_options: cp.dropdownOptions,
        setting: cp.setting,
        values: cp.values,
        is_enabled: cp.enabled !== false,
      }));
  }

  const ad_types = adTypes.map(({ publisher, adInputs }) => ({
    publisher,
    ad_inputs: adInputs.map(({ name, mediaType }) => ({
      media_type: mediaType,
      name,
      parameters: buildParametersForInput(publisher, name, mediaType),
      custom_parameters: buildCustomParameters(
        customParameters,
        publisher,
        name
      ),
    })),
  }));

  return {
    name: adCategory,
    publisher_type:
      publisherType === "single_publisher"
        ? "single"
        : publisherType === "multiple_publisher"
        ? "multi"
        : publisherType,
    publishers: publishers.map((e) => e.name),
    ad_types,
  };
}

function updateDataConvertToAPIFormat(
  helperInput: HelperInput
): UpdateCampaignOutput {
  const {
    adCategory,
    publisherType,
    publishers,
    adTypes,
    parametersInput,
    parametersData,
    customParameters,
  } = helperInput;

  function findParamDef(
    mediaType: string,
    groupKey: string
  ): ParameterDefinition | undefined {
    const defs = parametersData[mediaType.toLowerCase()] || [];
    return defs.find((def) => def.key === groupKey);
  }

  function buildParametersForInput(
    publisher: string | null,
    inputName: string,
    mediaType: string
  ) {
    const prefix = publisher
      ? `${publisher}_${inputName}`
      : `null_${inputName}`;
    const matchedParams = parametersInput.filter((param) =>
      param.key.startsWith(prefix)
    );

    return matchedParams
      .map((param) => {
        const groupKey = param.key.replace(`${prefix}_`, "");
        const paramDef = findParamDef(mediaType, groupKey);
        if (!paramDef) return null;

        // Skip params with no setting selected — backend requires value_type
        if (!param.settings || param.settings.trim() === "") return null;

        const inputs = param.inputs || [];
        const isUserDefined = param.settings === "user_defined";
        const isMultiPublisher = param.settings === "multi_publisher";
        const isSinglePublisher = param.settings === "single_publisher";

        if (isMultiPublisher) {
          const groupedInputs: Record<string, any[]> = {};

          inputs.forEach((inputObj) => {
            for (const key in inputObj) {
              if (!groupedInputs[key]) groupedInputs[key] = [];
              groupedInputs[key].push(Number(inputObj[key]));
            }
          });

          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: groupedInputs[field.key] || [],
          }));

          const result: any = {
            group_key: groupKey,
            value_type: param.settings,
            fields,
            is_enabled: param.enabled !== false,
          };

          if (param.id) {
            result.id = param.id;
          }
          result.parameter_definition_id = paramDef.id;

          return result;
        }

        if (isUserDefined) {
          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
          }));

          const result: any = {
            group_key: groupKey,
            value_type: param.settings,
            fields,
            is_enabled: param.enabled !== false,
          };

          if (param.id) {
            result.id = param.id;
          }
          result.parameter_definition_id = paramDef.id;

          return result;
        }

        if (isSinglePublisher) {
          const flatInput = inputs[0] || {};
          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: flatInput[field.key] ?? null,
          }));

          const result: any = {
            group_key: groupKey,
            value_type: param.settings,
            fields,
            is_enabled: param.enabled !== false,
          };

          if (param.id) {
            result.id = param.id;
          }
          result.parameter_definition_id = paramDef.id;

          return result;
        }

        const fieldsArr = inputs.map((inputObj) =>
          paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: inputObj[field.key] ?? null,
          }))
        );

        const result: any = {
          group_key: groupKey,
          value_type: param.settings,
          fields: fieldsArr[0] || [],
          is_enabled: param.enabled !== false,
        };

        if (param.id) {
          result.id = param.id;
        }
        result.parameter_definition_id = paramDef.id;

        return result;
      })
      .filter(Boolean);
  }

  function buildCustomParameters(
    customParams: CustomParameter[],
    publisher: string | null,
    name: string
  ) {
    const prefix = publisher ? `${publisher}_${name}_` : `null_${name}_`;

    return customParams
      .filter((cp) => cp.key.startsWith(prefix))
      .map((cp) => {
        const result: any = {
          name: cp.name,
          type: cp.type,
          setting: cp.setting,
          values: cp.values,
          is_enabled: cp.enabled !== false,
        };

        if (cp.id) {
          result.id = cp.id;
        }

        if (cp.dropdownOptions) {
          result.dropdown_options = cp.dropdownOptions;
        }

        return result;
      });
  }

  const ad_types = adTypes.map(
    ({ id: adTypeId, publisher, publisherId, adInputs }) => {
      const adTypeResult: any = {
        publisher,
        ad_inputs: adInputs.map(({ id: adInputId, name, mediaType }) => {
          const adInputResult: any = {
            media_type: mediaType,
            name,
            parameters: buildParametersForInput(publisher, name, mediaType),
            custom_parameters: buildCustomParameters(
              customParameters,
              publisher,
              name
            ),
          };

          if (adInputId) {
            adInputResult.id = adInputId;
          }

          return adInputResult;
        }),
      };

      if (adTypeId) {
        adTypeResult.id = adTypeId;
      }

      if (publisherId) {
        adTypeResult.publisher_id = publisherId;
      }

      return adTypeResult;
    }
  );

  return {
    name: adCategory,
    publisher_type:
      publisherType === "single_publisher"
        ? "single"
        : publisherType === "multiple_publisher"
        ? "multi"
        : publisherType,
    publishers: publishers.map((p) => {
      const pub: any = { name: p.name };
      if (p.id) {
        pub.id = p.id;
      }
      return pub;
    }),
    ad_types,
  };
}

export { convertToAPIFormat, updateDataConvertToAPIFormat };
